import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  ACCESSIBILITY_LABEL_HU,
  DOCUMENT_TYPE_LABEL_HU,
  FOLDER_STATUS_LABEL_HU,
  LOCATION_TYPE_LABEL_HU,
  RECOGNITION_STATUS_LABEL_HU,
  RECOGNITION_TYPE_LABEL_HU,
  SPINE_CURRENCY_LABEL_HU,
  type SpineFieldName,
  type SpineView,
} from '@persped/shared';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  companyName,
  useCompanies,
  useFolder,
  useFolderHistory,
  useImageUrl,
  useRetryRecognition,
  useRequestNewRecognition,
  useSetPrimaryImage,
  useDeleteImage,
} from '../../api/queries';
import { EditTextCard } from './EditTextCard';
import { SpineFieldsCard } from './SpineFieldsCard';
import { ReprintLabelCard } from './ReprintLabelCard';
import { MoveFolderCard } from './MoveFolderCard';
import { EventTable } from '../../components/EventTable';
import { ProblemAlert } from '../../components/ProblemAlert';
import { formatTimestamp } from '../../format';

/**
 * Mappa adatlap (spec US-FOLDER-ACTION-1) + életút (US-FOLDER-ACTION-3).
 *
 * W2-ben OLVASÓ nézet. A gerincszöveg szerkesztése, az áthelyezés és az
 * újranyomtatás a W3-ba tartozik — az Androidon mindhárom már működik,
 * tehát itt nem blokkol semmit.
 */
export function FolderDetailPage() {
  const { barcode = '' } = useParams();
  const navigate = useNavigate();
  const folder = useFolder(barcode);
  const history = useFolderHistory(barcode);
  const imageUrl = useImageUrl(barcode);
  const retry = useRetryRecognition();
  const newRecognition = useRequestNewRecognition();
  const setPrimary = useSetPrimaryImage();
  const deleteImage = useDeleteImage();
  // A cégnév az ADATBÁZISBÓL (F1) — ismeretlen kódnál magát a kódot mutatja.
  const companies = useCompanies();
  /** A gerincadat-szerkesztő alapból csukva — a javítás kivétel, nem napi munka. */
  const [editSpine, setEditSpine] = useState(false);
  /** A törlésre kijelölt kép azonosítója — `null`, ha nincs folyamatban törlés. */
  const [torlendo, setTorlendo] = useState<string | null>(null);
  const [archivumNyitva, setArchivumNyitva] = useState(false);
  const [torlesIndok, setTorlesIndok] = useState('');

  /**
   * A SAS URL-t a kattintás pillanatában kérjük le, és azonnal új fülön
   * nyitjuk — sosem tároljuk el. Ha cache-elnénk, egy pár perccel későbbi
   * kattintás már lejárt linket nyitna.
   */
  function openImage(imageId: string) {
    imageUrl.mutate(imageId, {
      onSuccess: (data) => window.open(data.url, '_blank', 'noopener,noreferrer'),
    });
  }

  if (folder.isPending) {
    return (
      <Stack sx={{ py: 6, alignItems: 'center' }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (folder.isError) return <ProblemAlert error={folder.error} />;

  const f = folder.data;
  const loc = f.currentLocation;

  // ÉLŐ és ARCHÍV szétválasztva. A szerver `isPrimary`-ként adja vissza —
  // a mező neve régebbi, a jelentése ma az, hogy EZ az egyetlen élő
  // gerincfotó, a többi az archívum. A felületen ezért mindenütt az
  // „Aktuális" / „Archív" szó szerepel, sosem az „elsődleges".
  const eloKep = f.images.find((i) => i.isPrimary) ?? null;
  const archivKepek = f.images.filter((i) => !i.isPrimary);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => void navigate(-1)}>
          Vissza
        </Button>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
          {f.barcode}
        </Typography>
        <Chip
          variant="outlined"
          color={f.status === 'APPROVED' ? 'success' : 'default'}
          label={FOLDER_STATUS_LABEL_HU[f.status]}
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Row label="Gerincszöveg">
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {f.currentText ?? '—'}
            </Typography>
          </Row>
          <Row label="Gerincadatok">
            <Stack spacing={1}>
              <SpineFieldsView spine={f.spine} companyName={companyName(companies.data, f.spine.company)} />
              <Button size="small" onClick={() => setEditSpine((v) => !v)} sx={{ alignSelf: 'flex-start' }}>
                {editSpine ? 'Szerkesztés bezárása' : 'Szerkesztés'}
              </Button>
              {/*
                A szerkesztő OTT nyílik, ahol a gomb van (2026-08-14-i CR #4).
                Korábban a lap alján, a gerincszöveg-szerkesztő ALATT jelent
                meg — kb. egy teljes képernyővel lejjebb. A gomb megnyomására
                látszólag semmi nem történt: megnyílt, csak a látómezőn kívül.
              */}
              <Collapse in={editSpine} unmountOnExit>
                <SpineFieldsCard key={`spine-${f.rowVersion}`} folder={f} />
              </Collapse>
            </Stack>
          </Row>
          <Row label="Szöveg jóváhagyva">
            <Typography variant="body2">{f.isTextApproved ? 'Igen' : 'Nem'}</Typography>
          </Row>
          <Row label="Aktuális hely">
            {loc ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <RouterLink to={`/lokaciok/${loc.barcode}`} style={{ fontFamily: 'monospace' }}>
                  {loc.barcode}
                  {loc.palletLevel !== null ? `/${loc.palletLevel}` : ''}
                </RouterLink>
                <Typography variant="body2">
                  {loc.name} — {LOCATION_TYPE_LABEL_HU[loc.locationType]}
                </Typography>
                {loc.accessibility && (
                  <Chip size="small" variant="outlined" label={ACCESSIBILITY_LABEL_HU[loc.accessibility]} />
                )}
                {/*
                  A hely EGY lépés (G fázis). Korábban a raklap polcon is
                  állhatott, és a lánc két lépésből olvasódott végig; az
                  egymásba ágyazás megszűnt, ezért a fenti vonalkód és szint
                  már a teljes válasz arra, hogy hova kell menni a mappáért.
                */}
                <Typography variant="caption" color="text.secondary">
                  {formatTimestamp(loc.since)} óta
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="error">
                Nincs aktív lokáció.
              </Typography>
            )}
          </Row>
          <Row label="Létrehozva">
            <Typography variant="body2">
              {formatTimestamp(f.createdAt)} — {f.createdBy}
            </Typography>
          </Row>
        </Stack>
      </Paper>

      {/*
        `key`: a mentés után a friss `rowVersion`-nel újra kell teremteni a
        szerkesztőt, különben a következő mentés elavult verzióval menne, és
        feleslegesen ütközne.
      */}
      <EditTextCard key={`${f.barcode}-${f.rowVersion}`} folder={f} onReload={() => void folder.refetch()} />

      <MoveFolderCard folder={f} />

      <ReprintLabelCard folder={f} />

      {/*
        AZ ÉLŐ FOTÓ ÉS AZ ARCHÍVUM (2026-08-25-i igény).
        
        Mappánként EGY élő gerincfotó van. Az újrafotózás a TC22-n történik —
        ott van a kamera és a mappa —, és az új kép magától élővé válik, a
        korábbi pedig ide, az archívumba kerül. A weben ez a képernyő az
        archívum VISSZAKERESŐJE és a végleges törlés helye: az előbbihez nagy
        képernyő kell, az utóbbihoz nyugodt, kétlépéses megerősítés — egyik
        sem raktári, kesztyűs művelet.
      */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle1">Gerincfotó</Typography>
          {eloKep ? (
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip size="small" color="primary" label="Aktuális" />
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                {eloKep.isOriginal ? 'Eredeti gerincfotó' : 'Újrafotózva'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>
                {formatTimestamp(eloKep.capturedAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                {Math.round(eloKep.sizeBytes / 1024)} kB
              </Typography>
              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={() => openImage(eloKep.id)}
                disabled={imageUrl.isPending}
              >
                Megnyitás
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nincs fotó.
            </Typography>
          )}

          {archivKepek.length > 0 && (
            <>
              <Divider sx={{ pt: 1 }} />
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                  Archívum ({archivKepek.length})
                </Typography>
                <Button size="small" onClick={() => setArchivumNyitva((v) => !v)}>
                  {archivumNyitva ? 'Elrejtés' : 'Megjelenítés'}
                </Button>
              </Stack>
              <Collapse in={archivumNyitva}>
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    A korábbi gerincfotók. Megmaradnak és visszakereshetők; bármelyik
                    visszakapcsolható élővé, ha az újrafotózás rosszabb lett a réginél.
                  </Typography>
                  {archivKepek.map((img) => (
                    <Stack
                      key={img.id}
                      direction="row"
                      spacing={2}
                      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <Typography variant="body2" sx={{ minWidth: 140 }}>
                        {img.isOriginal ? 'Eredeti gerincfotó' : 'Újrafotózva'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>
                        {formatTimestamp(img.capturedAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                        {Math.round(img.sizeBytes / 1024)} kB
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => openImage(img.id)}
                        disabled={imageUrl.isPending}
                      >
                        Megnyitás
                      </Button>
                      <Button
                        size="small"
                        disabled={setPrimary.isPending}
                        onClick={() => setPrimary.mutate({ barcode: f.barcode, imageId: img.id })}
                      >
                        Legyen ez az aktuális
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        disabled={deleteImage.isPending}
                        onClick={() => setTorlendo(img.id)}
                      >
                        Végleges törlés
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              </Collapse>
            </>
          )}

          {imageUrl.isError && <ProblemAlert error={imageUrl.error} />}
          {setPrimary.isError && <ProblemAlert error={setPrimary.error} />}
          {deleteImage.isError && <ProblemAlert error={deleteImage.error} />}

          {/*
            A törlés a 12. alapelv szűk kivétele, ezért INDOKOT kér — nem
            formaság: ez az egyetlen pont, ahol a rendszerből tény tűnik el,
            és a miértnek akkor is meg kell maradnia, ha a kép már nincs.
          */}
          {torlendo && (
            <Stack spacing={1} sx={{ pt: 1 }}>
              <Alert severity="warning">
                A kép <strong>véglegesen</strong> törlődik a tárolóból — az archiválás önmagában
                nem elég, ha a képet egyáltalán nem szabad tárolni. Az aktuális gerincfotót nem
                lehet törölni: előbb készíts újat a TC22-vel, vagy kapcsolj vissza egy archív
                képet élővé.
              </Alert>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  label="A törlés indoka"
                  value={torlesIndok}
                  onChange={(e) => setTorlesIndok(e.target.value)}
                  sx={{ minWidth: 320 }}
                  placeholder="pl. GDPR — kolléga arca került a képre"
                />
                <Button
                  variant="contained"
                  color="warning"
                  disabled={deleteImage.isPending || torlesIndok.trim().length === 0}
                  onClick={() =>
                    deleteImage.mutate(
                      { barcode: f.barcode, imageId: torlendo, reason: torlesIndok.trim() },
                      { onSuccess: () => { setTorlendo(null); setTorlesIndok(''); } },
                    )
                  }
                >
                  Végleges törlés
                </Button>
                <Button onClick={() => setTorlendo(null)}>Mégse</Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Paper>

      {(f.recognitions.length > 0 || f.images.length > 0) && (
        <Paper variant="outlined">
          <Stack direction="row" spacing={2} sx={{ p: 2, pb: 1, alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Felismerések
            </Typography>
            {/*
              ÚJ futtatás (2026-08-14-i CR #24) — nem azonos az
              „Újraindítás"-sal: az egy meglévő sort állít vissza és felülírja
              a korábbi eredményt. Ez új sort hoz létre, hogy a régi és az új
              egymás mellett maradjon; enélkül nem lehetne megmondani, hogy a
              paraméterváltás javított-e.
            */}
            <Button
              variant="outlined"
              size="small"
              disabled={newRecognition.isPending || f.images.length === 0}
              onClick={() => newRecognition.mutate({ barcode: f.barcode })}
            >
              Felismerés újrafuttatása
            </Button>
          </Stack>
          <Divider />
          {(retry.isError || newRecognition.isError) && (
            <div style={{ padding: 16 }}>
              <ProblemAlert error={retry.error ?? newRecognition.error} />
            </div>
          )}
          {newRecognition.isSuccess && (
            <div style={{ padding: 16, paddingBottom: 0 }}>
              <Alert severity="info" onClose={() => newRecognition.reset()}>
                Az új felismerés sorba került. Az eredmény néhány másodperc múlva megjelenik —
                a régi sor <strong>megmarad</strong>, hogy összehasonlítható legyen.
              </Alert>
            </div>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Motor</TableCell>
                <TableCell>Állapot</TableCell>
                <TableCell>Megbízhatóság</TableCell>
                <TableCell>Felismert szöveg</TableCell>
                <TableCell>Befejezve</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {f.recognitions.map((r) => (
                <TableRow
                  key={r.id}
                  sx={r.status === 'FAILED' ? { backgroundColor: 'rgba(211, 47, 47, 0.07)' } : undefined}
                >
                  <TableCell>{RECOGNITION_TYPE_LABEL_HU[r.recognitionType]}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={
                        r.status === 'COMPLETED' ? 'success' : r.status === 'FAILED' ? 'error' : 'default'
                      }
                      label={RECOGNITION_STATUS_LABEL_HU[r.status]}
                    />
                  </TableCell>
                  <TableCell>
                    {r.confidence !== null ? `${Math.round(r.confidence * 100)}%` : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>
                    {r.rawText ?? '—'}
                  </TableCell>
                  <TableCell>{formatTimestamp(r.completedAt)}</TableCell>
                  <TableCell>
                    {/*
                      Csak a SIKERTELEN felismerés indítható újra
                      (US-FOLDER-5 AC5) — a szerver `RECOGNITION_NOT_RETRIABLE`-t
                      adna egy futóra vagy késznek jelöltre.
                    */}
                    {r.status === 'FAILED' && (
                      <Button
                        size="small"
                        disabled={retry.isPending}
                        onClick={() => retry.mutate({ barcode: f.barcode, recognitionId: r.id })}
                      >
                        Újraindítás
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Paper variant="outlined">
        <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
          Életút
        </Typography>
        <Divider />
        {history.isError && <ProblemAlert error={history.error} />}
        <EventTable events={history.data ?? []} showEntityColumns={false} />
      </Paper>
    </Stack>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" sx={{ minWidth: 160, color: 'text.secondary', flexShrink: 0 }}>
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

/**
 * A gerincszövegből származtatott strukturált mezők (C3).
 *
 * Csak a KITÖLTÖTT mezőket mutatja — a legtöbb gerincen nincs mind a hét,
 * és az üres sorok tömege elnyomná a valódi tartalmat. A cég kivétel: az
 * mindig látszik, mert sosem üres (`Besorolatlan` a legrosszabb eset).
 */
function SpineFieldsView({ spine, companyName: cegNev }: { spine: SpineView; companyName: string }) {
  const periodText = (from: string | null, to: string | null): string | null => {
    if (!from) return null;
    return from === to ? from : `${from} – ${to ?? '?'}`;
  };

  /**
   * `fields`: mely MEZŐNEVEK adják ezt a chipet.
   *
   * ⚠ A kiemelés korábban a magyar CÍMKÉT kereste a `manualFields`-ben
   * (`includes('Cég')`), az viszont mezőneveket tárol (`company`) — a jelölés
   * tehát sosem gyulladt ki. Eddig ez nem látszott, mert a `manualFields`
   * mindig üres volt; az E4 óta valódi tartalma van.
   */
  const items: Array<[string, string | null, SpineFieldName[]]> = [
    ['Cég', cegNev, ['company']],
    [
      'Irattípus',
      spine.documentType ? DOCUMENT_TYPE_LABEL_HU[spine.documentType] : null,
      ['documentType'],
    ],
    ['Pénznem', spine.currency ? SPINE_CURRENCY_LABEL_HU[spine.currency] : null, ['currency']],
    [
      'Év',
      periodText(spine.yearFrom?.toString() ?? null, spine.yearTo?.toString() ?? null),
      ['yearFrom', 'yearTo'],
    ],
    ['Időszak', periodText(spine.periodFrom, spine.periodTo), ['periodFrom', 'periodTo']],
    [
      'Tartomány',
      spine.numberFrom ? `${spine.numberFrom} – ${spine.numberTo ?? '?'}` : null,
      ['numberFrom', 'numberTo'],
    ],
    ['Kötet', spine.volume?.toString() ?? null, ['volume']],
  ];

  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {items
        .filter(([, value]) => value !== null)
        .map(([label, value, fields]) => (
          <Chip
            key={label}
            size="small"
            variant="outlined"
            label={`${label}: ${value}`}
            // A kézzel felülbírált mezőt megkülönböztetjük: azt az
            // újraszámolás nem bántja, tehát más a jelentése.
            color={fields.some((f) => spine.manualFields.includes(f)) ? 'primary' : 'default'}
          />
        ))}
    </Stack>
  );
}
